/**
 * Learning analytics — everything the dashboards, the parent report and the
 * coaching tips are derived from.
 *
 * All of it is computed on demand from the session log and the key tally. No
 * derived numbers are ever stored, so a stat can never drift away from the
 * data that produced it.
 */
import type { KidProfile, LevelDef, SessionRecord } from '../types';
import { LEVELS, ALL_LESSONS } from '../data/curriculum';
import { charLabel, fingerForChar, FINGERS } from './keyboard';
import { dayKey, daysBetween, completedCount, totalStars, MAX_STARS } from './progress';
import { benchmarkFor, isQualifyingRun } from './metrics';

/* ────────────────────────────────────────────────────────────── overview ── */

export interface Overview {
  bestWpm: number;
  averageWpm: number;
  /** Mean of the ten most recent sessions — the "where you are now" number. */
  recentWpm: number;
  averageAccuracy: number;
  recentAccuracy: number;
  totalMinutes: number;
  totalSessions: number;
  practiceDays: number;
  lessonsDone: number;
  lessonsTotal: number;
  stars: number;
  starsMax: number;
  /** Percent change in WPM between the first and last third of the log. */
  trendPct: number;
}

const mean = (values: number[]) =>
  values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;

export function overview(profile: KidProfile | null): Overview {
  const sessions = profile?.sessions ?? [];
  const wpms = sessions.map((s) => s.wpm);
  const recent = sessions.slice(-10);

  // "Best speed" should come from a run long enough to mean something — a
  // 60-character warm-up is not a personal record. Before any qualifying run
  // exists we still show the best of what there is, so a beginner sees a real
  // number rather than a zero.
  const qualifying = sessions.filter(isQualifyingRun).map((s) => s.wpm);
  const bestWpm = qualifying.length ? Math.max(...qualifying) : wpms.length ? Math.max(...wpms) : 0;

  // Compare the earliest third with the latest third; needs at least six runs
  // before it means anything, otherwise a single lucky session dominates.
  let trendPct = 0;
  if (sessions.length >= 6) {
    const slice = Math.floor(sessions.length / 3);
    const first = mean(sessions.slice(0, slice).map((s) => s.wpm));
    const last = mean(sessions.slice(-slice).map((s) => s.wpm));
    if (first > 0) trendPct = Math.round(((last - first) / first) * 100);
  }

  return {
    bestWpm,
    averageWpm: Math.round(mean(wpms)),
    recentWpm: Math.round(mean(recent.map((s) => s.wpm))),
    averageAccuracy: Math.round(mean(sessions.map((s) => s.accuracy)) * 10) / 10,
    recentAccuracy: Math.round(mean(recent.map((s) => s.accuracy)) * 10) / 10,
    totalMinutes: Math.round(sessions.reduce((sum, s) => sum + s.seconds, 0) / 60),
    totalSessions: sessions.length,
    practiceDays: new Set(sessions.map((s) => s.day)).size,
    lessonsDone: completedCount(profile),
    lessonsTotal: ALL_LESSONS.length,
    stars: totalStars(profile),
    starsMax: MAX_STARS,
    trendPct,
  };
}

/* ──────────────────────────────────────────────────────────── key health ── */

export interface KeyHealth {
  char: string;
  label: string;
  attempts: number;
  accuracy: number;
  finger: string;
  fingerLabel: string;
}

const MIN_ATTEMPTS = 6; // below this a single slip looks like a catastrophe

export function keyHealth(profile: KidProfile | null): KeyHealth[] {
  if (!profile) return [];
  return Object.entries(profile.keyStats)
    .map(([char, stat]) => {
      const attempts = stat.correct + stat.wrong;
      const finger = fingerForChar(char);
      return {
        char,
        label: charLabel(char),
        attempts,
        accuracy: attempts ? Math.round((stat.correct / attempts) * 100) : 100,
        finger,
        fingerLabel: FINGERS[finger].label,
      };
    })
    .sort((a, b) => a.accuracy - b.accuracy || b.attempts - a.attempts);
}

/** The keys that genuinely need work — enough attempts to be meaningful. */
export function weakKeys(profile: KidProfile | null, limit = 6): KeyHealth[] {
  return keyHealth(profile)
    .filter((k) => k.attempts >= MIN_ATTEMPTS && k.accuracy < 92)
    .slice(0, limit);
}

export function strongKeys(profile: KidProfile | null, limit = 6): KeyHealth[] {
  return keyHealth(profile)
    .filter((k) => k.attempts >= MIN_ATTEMPTS)
    .slice()
    .reverse()
    .slice(0, limit);
}

/** Accuracy grouped by finger — shows *which hand* is holding a child back. */
export function fingerHealth(profile: KidProfile | null) {
  const buckets = new Map<string, { correct: number; wrong: number }>();
  for (const [char, stat] of Object.entries(profile?.keyStats ?? {})) {
    const finger = fingerForChar(char);
    const acc = buckets.get(finger) ?? { correct: 0, wrong: 0 };
    buckets.set(finger, { correct: acc.correct + stat.correct, wrong: acc.wrong + stat.wrong });
  }
  return Object.values(FINGERS)
    .map((f) => {
      const stat = buckets.get(f.id) ?? { correct: 0, wrong: 0 };
      const attempts = stat.correct + stat.wrong;
      return {
        id: f.id,
        label: f.label,
        hand: f.hand,
        cssVar: f.cssVar,
        attempts,
        accuracy: attempts ? Math.round((stat.correct / attempts) * 100) : 0,
      };
    })
    .filter((f) => f.attempts > 0);
}

/* ─────────────────────────────────────────────────────────────── history ── */

export interface DayPoint {
  day: string;
  /** "Mon 14" — the axis label. */
  label: string;
  minutes: number;
  wpm: number;
  accuracy: number;
  sessions: number;
}

/** One point per day for the last `days` days, including days with no practice. */
export function dailyHistory(profile: KidProfile | null, days = 30): DayPoint[] {
  const byDay = new Map<string, SessionRecord[]>();
  for (const session of profile?.sessions ?? []) {
    const list = byDay.get(session.day) ?? [];
    list.push(session);
    byDay.set(session.day, list);
  }

  const out: DayPoint[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const key = dayKey(date);
    const list = byDay.get(key) ?? [];
    out.push({
      day: key,
      label: date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' }),
      minutes: Math.round(list.reduce((s, r) => s + r.seconds, 0) / 60),
      wpm: list.length ? Math.round(mean(list.map((r) => r.wpm))) : 0,
      accuracy: list.length ? Math.round(mean(list.map((r) => r.accuracy))) : 0,
      sessions: list.length,
    });
  }
  return out;
}

/** Session-by-session speed, for the "am I improving?" line chart. */
export function speedSeries(profile: KidProfile | null, limit = 40) {
  return (profile?.sessions ?? []).slice(-limit).map((s, i) => ({
    index: i + 1,
    wpm: s.wpm,
    accuracy: s.accuracy,
    label: new Date(s.iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  }));
}

/** Per-level completion, for the skill graph. */
export function levelProgress(profile: KidProfile | null) {
  return LEVELS.map((level: LevelDef) => {
    const done = level.lessons.filter((l) => profile?.lessons[l.id]?.completed).length;
    const stars = level.lessons.reduce((sum, l) => sum + (profile?.lessons[l.id]?.stars ?? 0), 0);
    return {
      id: level.id,
      index: level.index,
      title: level.title,
      emoji: level.emoji,
      color: level.color,
      done,
      total: level.lessons.length,
      stars,
      starsMax: level.lessons.length * 3,
      pct: Math.round((done / level.lessons.length) * 100),
    };
  });
}

/* ─────────────────────────────────────────────────── coaching suggestions ── */

export interface Suggestion {
  id: string;
  emoji: string;
  title: string;
  body: string;
  /** Where to send the child to act on it. */
  href?: string;
  cta?: string;
  tone: 'praise' | 'tip' | 'warn';
}

/**
 * Rule-based coaching. Deliberately not a black box: every suggestion states
 * the number that triggered it, so a parent or teacher can check the reasoning
 * and a child can see exactly what to change.
 */
export function suggestions(profile: KidProfile | null): Suggestion[] {
  const out: Suggestion[] = [];
  if (!profile || profile.sessions.length === 0) {
    return [
      {
        id: 'start',
        emoji: '🚀',
        title: 'Start with Home Base',
        body: 'Level 1 teaches F and J — the two keys with little bumps that your pointer fingers live on. It takes about three minutes.',
        href: '/kids/journey',
        cta: 'Open the journey',
        tone: 'tip',
      },
    ];
  }

  const stats = overview(profile);
  const weak = weakKeys(profile, 3);
  const today = dayKey();
  const sinceLast = profile.streak.lastDay ? daysBetween(profile.streak.lastDay, today) : 99;

  if (stats.recentAccuracy < 92) {
    out.push({
      id: 'accuracy',
      emoji: '🎯',
      title: 'Slow down a little',
      body: `Your last ten lessons averaged ${stats.recentAccuracy}% accuracy. Aim for 95% before pushing for speed — every mistake costs more time than it saves.`,
      tone: 'warn',
    });
  } else if (stats.recentAccuracy >= 97) {
    out.push({
      id: 'accuracy-good',
      emoji: '💎',
      title: 'Your accuracy is excellent',
      body: `${stats.recentAccuracy}% across your last ten lessons. That is the hard part done — now let the speed come.`,
      tone: 'praise',
    });
  }

  if (weak.length) {
    const list = weak.map((k) => `“${k.label}”`).join(', ');
    const fingers = Array.from(new Set(weak.map((k) => k.fingerLabel)));
    out.push({
      id: 'weak-keys',
      emoji: '🔧',
      title: `Three keys to fix: ${list}`,
      body: `These are your least accurate keys${
        fingers.length === 1 ? `, all typed with your ${fingers[0]}` : ''
      }. Replay the level that introduced them — five minutes there is worth an hour of general practice.`,
      href: '/kids/journey',
      cta: 'Practise them',
      tone: 'tip',
    });
  }

  if (sinceLast >= 2) {
    out.push({
      id: 'gap',
      emoji: '📅',
      title: sinceLast > 7 ? 'Welcome back!' : 'Keep the streak alive',
      body:
        sinceLast > 7
          ? `It has been ${sinceLast} days. Start with a lesson you have already finished to warm your fingers up.`
          : 'A short lesson today keeps your fingers sharp. Ten minutes beats an hour on Sunday.',
      href: '/kids/journey',
      cta: 'Practise now',
      tone: sinceLast > 7 ? 'tip' : 'warn',
    });
  }

  if (stats.trendPct >= 10) {
    out.push({
      id: 'trend',
      emoji: '📈',
      title: `You are ${stats.trendPct}% faster than when you started`,
      body: `Your speed has gone from the low ${Math.max(1, Math.round(stats.recentWpm / (1 + stats.trendPct / 100)))} range up to ${stats.recentWpm} words per minute. That is real progress.`,
      tone: 'praise',
    });
  }

  const benchmark = benchmarkFor(profile.ageBand);
  if (profile.ageBand !== 'unspecified' && stats.recentWpm > 0) {
    out.push({
      id: 'benchmark',
      emoji: '🧭',
      title:
        stats.recentWpm >= benchmark.strong
          ? 'Ahead of the typical pace for your age'
          : 'Right on track',
      body: `For ${benchmark.label.toLowerCase()}, around ${benchmark.typical} words per minute is typical and ${benchmark.strong} is strong. You are at ${stats.recentWpm}.`,
      tone: stats.recentWpm >= benchmark.typical ? 'praise' : 'tip',
    });
  }

  if (stats.totalSessions >= 3 && stats.totalMinutes / stats.practiceDays > 25) {
    out.push({
      id: 'session-length',
      emoji: '⏳',
      title: 'Try shorter, more frequent sessions',
      body: `You average about ${Math.round(stats.totalMinutes / stats.practiceDays)} minutes per practice day. Two 10-minute sessions build muscle memory better than one long one.`,
      tone: 'tip',
    });
  }

  return out.slice(0, 4);
}

/* ─────────────────────────────────────────────────────────────── reports ── */

export interface PeriodReport {
  label: string;
  from: string;
  to: string;
  sessions: number;
  minutes: number;
  lessons: number;
  averageWpm: number;
  averageAccuracy: number;
  stars: number;
  bestWpm: number;
  activeDays: number;
}

/** Aggregate the last `days` days — powers the weekly/monthly parent report. */
export function periodReport(
  profile: KidProfile | null,
  days: number,
  label: string
): PeriodReport {
  const today = dayKey();
  const inRange = (profile?.sessions ?? []).filter((s) => daysBetween(s.day, today) < days);
  const wpms = inRange.map((s) => s.wpm);

  return {
    label,
    from: inRange[0]?.day ?? today,
    to: today,
    sessions: inRange.length,
    minutes: Math.round(inRange.reduce((sum, s) => sum + s.seconds, 0) / 60),
    lessons: new Set(inRange.map((s) => s.lessonId)).size,
    averageWpm: Math.round(mean(wpms)),
    averageAccuracy: Math.round(mean(inRange.map((s) => s.accuracy)) * 10) / 10,
    stars: inRange.reduce((sum, s) => sum + s.stars, 0),
    bestWpm: wpms.length ? Math.max(...wpms) : 0,
    activeDays: new Set(inRange.map((s) => s.day)).size,
  };
}

/** CSV for the teacher export. Values are escaped; no formula injection. */
export function toCsv(rows: Record<string, string | number>[]): string {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  const escape = (value: string | number) => {
    const text = String(value);
    // Leading =, +, - or @ can be interpreted as a formula by spreadsheets.
    const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  return [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => escape(row[h] ?? '')).join(',')),
  ].join('\n');
}
